import React, { Suspense, useCallback, useEffect, useState } from "react";
import { Center, Link, Tabs, Text, Spinner } from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router";
import { useQuery } from "@apollo/client";
import {
  EMAILS_FOR_REVIEW,
  FILL_APPLICATION_FORM,
  FIND_ENTITY_ID,
  SELECT_AGENT,
  SELECT_APPLICATION_FORM,
  SELECT_BANK_STATEMENTS,
} from "@/constants/routes";
import { REVIEW_EMAIL } from "@/api/queries";
import {
  IEmailAIExtractedData,
  IEmailFile,
} from "@/types/common";
import ManualReviewCaseResolvedDialog from "./components/ManualReviewCaseResolvedDialog";

const FillApplicationForm = React.lazy(() => import("./pages/fill-application-form/FillApplicationForm"));
const SelectApplicationForm = React.lazy(() => import("./pages/select-application-form/SelectApplicationForm"));
const SelectBankStatements = React.lazy(() => import("./pages/select-bank-statements/SelectBankStatements"));
const FindEntityId = React.lazy(() => import("./pages/find-entity-id/FindEntityId"));
const SelectAgent = React.lazy(() => import("@/features/emails-for-review/pages/resolve-manually/pages/select-agent/SelectAgent"));


const ResolveManually = () => {
  const navigate = useNavigate();

  const { state } = useLocation();
  const reviewEmailId = state?.reviewEmail?.id;

  const [tab, setTab] = useState(SELECT_APPLICATION_FORM);
  const { data, loading } = useQuery(REVIEW_EMAIL, {
    variables: { emailId: reviewEmailId },
    skip: !reviewEmailId,
  });

  const reviewEmail: IEmailAIExtractedData = data?.reviewEmail
    ? data?.reviewEmail
    : state?.reviewEmail;
  const [selectedEmailFiles, setSelectedEmailFiles] = useState<IEmailFile[]>([]);
  const [completionModal, setCompletionModal] = useState<
    null | "resolved" | "documentBroken"
  >(null);

  const onCaseFullyResolved = useCallback(() => setCompletionModal("resolved"), []);
  const onDocumentMarkedBroken = useCallback(
    () => setCompletionModal("documentBroken"),
    [],
  );

  useEffect(() => {
    navigate(tab, { replace: true, state: { ...state, reviewEmail } });
  }, [tab, reviewEmail]);

  if (loading) {
    return (
      <Center
        w="100%"
        border="1px solid"
        borderColor="border"
        rounded="md"
        minH="500px"
      >
        <Spinner size="xl" color="brand.solid" />
      </Center>
    );
  }

  if (!reviewEmail) {
    return (
      <Center
        w="100%"
        border="1px solid"
        borderColor="border"
        rounded="md"
        minH="500px"
      >
        <Text>
          Review email is not selected.{" "}
          <Link href={EMAILS_FOR_REVIEW} color="brand.solid">
            Select email for review
          </Link>
        </Text>
      </Center>
    );
  }

  return (
    <>
      <ManualReviewCaseResolvedDialog
        open={completionModal != null}
        variant={completionModal === "documentBroken" ? "documentBroken" : "resolved"}
        onOpenChange={(open) => {
          if (!open) setCompletionModal(null);
        }}
      />
    <Tabs.Root
      value={tab}
      onValueChange={(e: { value: string }) => setTab(e.value)}
      isLazy
    >
      <Tabs.List>
        <Tabs.Trigger
          value={SELECT_APPLICATION_FORM}
          color={!reviewEmail?.isSelectApplicationForm ? "red.500" : "inherit"}
        >
          Select application form
        </Tabs.Trigger>

        <Tabs.Trigger
          value={FILL_APPLICATION_FORM}
          color={!reviewEmail?.isFillApplicationForm ? "red.500" : "inherit"}
        >
          Fill application form
        </Tabs.Trigger>

        <Tabs.Trigger
          value={SELECT_BANK_STATEMENTS}
          color={!reviewEmail?.isSelectBankStatement ? "red.500" : "inherit"}
        >
          Select bank statements
        </Tabs.Trigger>

        <Tabs.Trigger
          value={SELECT_AGENT}
          color={!reviewEmail?.isSelectAgent ? "red.500" : "inherit"}
        >
          Select agent
        </Tabs.Trigger>
      </Tabs.List>

      <TabPanel value={SELECT_APPLICATION_FORM}>
        <SelectApplicationForm
          reviewEmail={reviewEmail}
          selectedEmailFiles={selectedEmailFiles}
          setSelectedEmailFiles={setSelectedEmailFiles}
          setTab={setTab}
          onCaseFullyResolved={onCaseFullyResolved}
          onDocumentMarkedBroken={onDocumentMarkedBroken}
        />
      </TabPanel>
      <TabPanel value={FILL_APPLICATION_FORM}>
        <FillApplicationForm
          reviewEmail={reviewEmail}
          setTab={setTab}
          selectedEmailFiles={selectedEmailFiles}
          setSelectedEmailFiles={setSelectedEmailFiles}
          onCaseFullyResolved={onCaseFullyResolved}
          onDocumentMarkedBroken={onDocumentMarkedBroken}
        />
      </TabPanel>
      <TabPanel value={SELECT_BANK_STATEMENTS}>
        <SelectBankStatements
          reviewEmail={reviewEmail}
          setTab={setTab}
          onCaseFullyResolved={onCaseFullyResolved}
          onDocumentMarkedBroken={onDocumentMarkedBroken}
        />
      </TabPanel>
      <TabPanel value={FIND_ENTITY_ID}>
        <FindEntityId
          reviewEmail={reviewEmail}
          setTab={setTab}
          onCaseFullyResolved={onCaseFullyResolved}
          onDocumentMarkedBroken={onDocumentMarkedBroken}
        />
      </TabPanel>
      <TabPanel value={SELECT_AGENT}>
        <SelectAgent
          reviewEmail={reviewEmail}
          setTab={setTab}
          onCaseFullyResolved={onCaseFullyResolved}
          onDocumentMarkedBroken={onDocumentMarkedBroken}
        />
      </TabPanel>
    </Tabs.Root>
    </>
  );
};

const TabPanel = ({ children, ...props }: any) => (
  <Tabs.Content {...props} pt={8}>
    {children}
  </Tabs.Content>
);

export default ResolveManually;
